import { Company } from '../stores/types';
import { PayterTerminal } from '../stores/types/payter.interface';
import { Api, API_KEYS } from './base.api';
import { downloadBlob, providesListTag } from './utils';

export const payoutApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    exportPayoutReportUserTransactionPDF: builder.query<Blob, any>({
      query: (params: { companyId: string; yearMonth: string; scope: string }) => ({
        url: `/internal/core/v2/${API_KEYS.HISTORICAL}/${API_KEYS.PAYOUTS}/${API_KEYS.STATEMENT}`,
        params: {
          scope: params.scope,
          companyId: params.companyId,
          yearMonth: params.yearMonth,
        },
        method: 'GET',
        isPDF: true,
        responseHandler: async (response) => {
          const blob = await response.blob();
          if (blob.size <= 0) return;
          const cd = response.headers.get('content-disposition') || '';
          let filename = 'Sessions Report';
          const match = cd.match(/filename="?([^"]+)"?/i);
          if (match && match[1]) {
            filename = match[1].trim();
          }
          filename = filename.replace(/^_+/, '').replace(/\.pdf$/i, '');
          downloadBlob(blob, filename, 'pdf');
        },
      }),
    }),
    fetchCombinedTerminals: builder.query<PayterTerminal, any>({
      query: (params) => ({
        url: `/internal/core/v2/${API_KEYS.PAYMENT_TERMINALS_COMBINED}`,
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        providesListTag(
          result?.entities?.map((terminal) => ({ id: terminal.id })),
          API_KEYS.PAYMENT_TERMINALS_COMBINED,
        ),
    }),

    fetchTerminals: builder.query<PayterTerminal, any>({
      query: () => ({
        url: `/internal/core/v2/${API_KEYS.PAYMENT_TERMINALS}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        providesListTag(
          result?.entities?.map((terminal) => ({ id: terminal.id })),
          API_KEYS.PAYMENT_TERMINALS,
        ),
    }),

    editTerminalsById: builder.mutation<PayterTerminal, any>({
      query: (params) => ({
        url: `/internal/core/v2/${API_KEYS.PAYMENT_TERMINALS}/${params.body.terminalId}`,
        method: 'PATCH',
        body: params.body,
        toast: params.toast,
      }),
      invalidatesTags: [API_KEYS.PAYMENT_TERMINALS_COMBINED],
    }),
    linkTerminals: builder.mutation<PayterTerminal, any>({
      query: (params) => ({
        url: `/internal/core/v2/${API_KEYS.PAYMENT_TERMINALS}`,
        method: 'POST',
        body: params.body,
        toast: params.toast,
      }),
      invalidatesTags: [API_KEYS.PAYMENT_TERMINALS_COMBINED],
    }),
    deleteTerminals: builder.mutation<PayterTerminal, any>({
      query: (params) => ({
        url: `/internal/core/v2/${API_KEYS.PAYMENT_TERMINALS}/${params.body.terminalId}`,
        method: 'DELETE',
        toast: params.toast,
      }),
      invalidatesTags: [API_KEYS.PAYMENT_TERMINALS_COMBINED],
    }),

    unlinkPayterAccount: builder.mutation<PayterTerminal, any>({
      query: () => ({
        url: `/internal/core/v2/${API_KEYS.PAYMENT_TERMINALS}`,
        method: 'DELETE',
      }),
      invalidatesTags: [API_KEYS.PAYMENT_TERMINALS_COMBINED],
    }),

    updatePayterIdentifier: builder.mutation<Company, any>({
      query: (payload) => ({
        url: `/internal/core/v2/${API_KEYS.COMPANIES}/${payload.body.id}`,
        body: payload.body,
        method: 'PATCH',
        toast: payload.toast,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: API_KEYS.COMPANIES, id: 'LIST' },
        { type: API_KEYS.COMPANIES, id: result?.id },
        { type: API_KEYS.PAYMENT_TERMINALS_COMBINED },
      ],
    }),
    updatePaymentTerminalInfoVisiblity: builder.mutation<Company, any>({
      query: (payload) => ({
        url: `/internal/core/v2/${API_KEYS.COMPANIES}/${payload.body.id}`,
        body: payload.body,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, arg) => [
        { type: API_KEYS.COMPANIES, id: 'LIST' },
        { type: API_KEYS.COMPANIES, id: result?.id },
      ],
    }),
  }),
});

export const {
  useLazyExportPayoutReportUserTransactionPDFQuery,
  useFetchTerminalsQuery,
  useFetchCombinedTerminalsQuery,
  useEditTerminalsByIdMutation,
  useLinkTerminalsMutation,
  useUpdatePayterIdentifierMutation,
  useDeleteTerminalsMutation,
  useUnlinkPayterAccountMutation,
  useUpdatePaymentTerminalInfoVisiblityMutation,
} = payoutApi;
